// Shim of the small chrome.* surface the side-panel React code touches so
// the same components run unmodified inside a regular webapp tab. We do
// NOT shim chrome.runtime.sendMessage — that path is intercepted at the
// `send()` helper in src/shared/messages.ts and routed to fetch instead.

type StorageChange = chrome.storage.StorageChange;
type Listener = (
  changes: Record<string, StorageChange>,
  area: 'local' | 'sync' | 'managed' | 'session'
) => void;

const LS_PREFIX = 'tishnotes:';
const listeners = new Set<Listener>();

function localStorageBacked(area: 'local'): chrome.storage.StorageArea {
  return {
    async get(keys?: string | string[] | { [key: string]: unknown } | null) {
      const out: Record<string, unknown> = {};
      const collect = (k: string) => {
        const raw = localStorage.getItem(LS_PREFIX + k);
        if (raw === null) return;
        try {
          out[k] = JSON.parse(raw);
        } catch {
          out[k] = raw;
        }
      };
      if (keys === undefined || keys === null) {
        for (let i = 0; i < localStorage.length; i++) {
          const lk = localStorage.key(i);
          if (lk && lk.startsWith(LS_PREFIX)) {
            collect(lk.slice(LS_PREFIX.length));
          }
        }
      } else if (typeof keys === 'string') {
        collect(keys);
      } else if (Array.isArray(keys)) {
        for (const k of keys) collect(k);
      } else {
        for (const k of Object.keys(keys)) {
          collect(k);
          if (out[k] === undefined) {
            out[k] = (keys as Record<string, unknown>)[k];
          }
        }
      }
      return out;
    },
    async set(items: Record<string, unknown>) {
      const changes: Record<string, StorageChange> = {};
      for (const [k, v] of Object.entries(items)) {
        const oldRaw = localStorage.getItem(LS_PREFIX + k);
        let oldValue: unknown;
        if (oldRaw !== null) {
          try {
            oldValue = JSON.parse(oldRaw);
          } catch {
            oldValue = oldRaw;
          }
        }
        localStorage.setItem(LS_PREFIX + k, JSON.stringify(v));
        changes[k] = { newValue: v, oldValue };
      }
      for (const l of listeners) l(changes, area);
    },
    async remove(keys: string | string[]) {
      const arr = Array.isArray(keys) ? keys : [keys];
      const changes: Record<string, StorageChange> = {};
      for (const k of arr) {
        const oldRaw = localStorage.getItem(LS_PREFIX + k);
        if (oldRaw === null) continue;
        let oldValue: unknown;
        try {
          oldValue = JSON.parse(oldRaw);
        } catch {
          oldValue = oldRaw;
        }
        localStorage.removeItem(LS_PREFIX + k);
        changes[k] = { oldValue };
      }
      for (const l of listeners) l(changes, area);
    },
    async clear() {
      const changes: Record<string, StorageChange> = {};
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const lk = localStorage.key(i);
        if (!lk || !lk.startsWith(LS_PREFIX)) continue;
        const raw = localStorage.getItem(lk);
        let oldValue: unknown;
        if (raw !== null) {
          try {
            oldValue = JSON.parse(raw);
          } catch {
            oldValue = raw;
          }
        }
        localStorage.removeItem(lk);
        changes[lk.slice(LS_PREFIX.length)] = { oldValue };
      }
      for (const l of listeners) l(changes, area);
    },
  } as chrome.storage.StorageArea;
}

const onChanged = {
  addListener(cb: Listener) {
    listeners.add(cb);
  },
  removeListener(cb: Listener) {
    listeners.delete(cb);
  },
  hasListener(cb: Listener) {
    return listeners.has(cb);
  },
  hasListeners() {
    return listeners.size > 0;
  },
  getRules() {},
  removeRules() {},
  addRules() {},
} as unknown as chrome.storage.StorageChangedEvent;

// runtimeListeners is the in-process broadcast bus for the webapp. SSE
// dispatchers (src/web/api.ts CHAT_ASK) push LLM_DELTA frames here, and
// route components subscribe via chrome.runtime.onMessage.addListener.
type RuntimeListener = (msg: unknown) => void;
const runtimeListeners = new Set<RuntimeListener>();

export function broadcastWebappEvent(msg: unknown): void {
  for (const l of runtimeListeners) {
    try {
      l(msg);
    } catch {
      // Listener errors must not stop other listeners.
    }
  }
}

export function installChromeShim(): void {
  if (typeof globalThis.chrome !== 'undefined' && (globalThis.chrome as { runtime?: unknown }).runtime) {
    return; // real extension context
  }
  // Intentionally NO `sendMessage` field on fakeRuntime: `send()` in
  // src/shared/messages.ts uses `!chrome.runtime?.sendMessage` to decide
  // whether to take the HTTP path. Defining a stub here (even one that
  // throws) breaks that detection and routes calls to the stub instead
  // of the dispatcher.
  const fakeRuntime = {
    openOptionsPage: async () => {
      window.location.assign('/settings');
    },
    onMessage: {
      addListener(cb: RuntimeListener) {
        runtimeListeners.add(cb);
      },
      removeListener(cb: RuntimeListener) {
        runtimeListeners.delete(cb);
      },
      hasListener(cb: RuntimeListener) {
        return runtimeListeners.has(cb);
      },
    },
  } as unknown as typeof chrome.runtime;

  (globalThis as unknown as { chrome: typeof chrome }).chrome = {
    runtime: fakeRuntime,
    storage: {
      local: localStorageBacked('local'),
      onChanged,
    },
  } as unknown as typeof chrome;

  // App.tsx gates rendering on hasKey || llmBackend==='local-shim'. The
  // server holds the real Anthropic key in webapp mode, so seed a marker
  // value that makes the gate pass without exposing anything sensitive.
  if (localStorage.getItem(LS_PREFIX + 'apiKey') === null) {
    localStorage.setItem(LS_PREFIX + 'apiKey', JSON.stringify('webapp-managed-server-side'));
  }
}
